/**
 * Token -> CSS custom property generation.
 *
 * Strategy: RUNTIME INJECTION (not a build-time codegen step).
 * Justification: the CRM is Create React App (react-scripts) with no eject/craco,
 * so there is no clean hook to run a codegen pre-build that writes theme.css. Emitting
 * the `:root` / `[data-theme="dark"]` blocks from tokens.js at runtime guarantees the
 * CSS variables are ALWAYS derived from the single source of truth with zero hand-
 * maintained duplication, and cannot drift. theme.css keeps only the rules that
 * *consume* var(--...); it no longer defines the values.
 */

const STYLE_ELEMENT_ID = 'theme-tokens';

/**
 * Flatten a nested token set into `{ '--group-key': value }`.
 * Group + key names are authored so `--group-key` matches the legacy variable
 * names theme.css consumers already reference.
 */
export function flattenTokens(tokenSet) {
  const out = {};
  for (const [group, entries] of Object.entries(tokenSet)) {
    for (const [key, value] of Object.entries(entries)) {
      out[`--${group}-${key}`] = value;
    }
  }
  return out;
}

function cssVarBlock(selector, tokenSet) {
  const vars = flattenTokens(tokenSet);
  const lines = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${lines}\n}`;
}

/**
 * Build the full stylesheet string: light values on :root, dark overrides on
 * [data-theme="dark"]. The dark block is the complete set (not a partial diff),
 * so switching modes never leaves a stale light value behind.
 */
export function buildThemeStylesheet({ light, dark }) {
  // Light values on :root AND [data-theme="light"] so a SUBTREE can re-assert light
  // tokens even while the app is dark — used by the unmigrated-route readability
  // stopgap (MainLayout puts not-yet-migrated <main> in a data-theme="light" scope).
  return `${cssVarBlock(':root, [data-theme="light"]', light)}\n\n${cssVarBlock('[data-theme="dark"]', dark)}\n`;
}

/**
 * Inject (or update) a single <style id="theme-tokens"> in the document head.
 * Idempotent: reuses the existing node on subsequent calls so there is never a
 * duplicate. Import tokens lazily to avoid a hard dependency in unit tests that
 * only exercise the pure functions above.
 */
export function applyThemeVars(doc = document) {
  // eslint-disable-next-line global-require
  const { lightTokens, darkTokens } = require('./tokens');
  let style = doc.getElementById(STYLE_ELEMENT_ID);
  if (!style) {
    style = doc.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    doc.head.appendChild(style);
  }
  style.textContent = buildThemeStylesheet({ light: lightTokens, dark: darkTokens });
  return style;
}
